import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import Logo from '../components/Logo';

const PENDING_KEY = 'menusore_pending_register';

function Register() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [storeName, setStoreName] = useState('');
  const [storeId, setStoreId] = useState('');
  const [idError, setIdError] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailOptIn, setEmailOptIn] = useState(false);
  const { register, loginWithGoogle, resolveGoogleRedirect, currentUser } = useAuth();
  const navigate = useNavigate();

  // Googleのリダイレクトから戻ってきた直後にエラーがあれば拾う
  useEffect(() => {
    (async () => {
      try {
        await resolveGoogleRedirect();
      } catch {
        setError('Googleログインに失敗しました。');
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ログイン状態が確定したら（Googleリダイレクト経由）保留していた登録情報で店舗を作成
  useEffect(() => {
    if (!currentUser) return;
    const pendingRaw = sessionStorage.getItem(PENDING_KEY);
    if (!pendingRaw) return; // 通常のメール登録など、Google登録待ちでなければ何もしない
    (async () => {
      try {
        const pending = JSON.parse(pendingRaw);
        if (!pending || !pending.storeId) {
          setError('登録情報の復元に失敗しました。お手数ですが店舗IDの入力からやり直してください。');
          sessionStorage.removeItem(PENDING_KEY);
          return;
        }
        setLoading(true);
        const idSnap = await getDoc(doc(db, 'storeIds', pending.storeId));
        if (idSnap.exists()) {
          setError('この店舗IDはすでに使われています。別のIDでお試しください。');
          sessionStorage.removeItem(PENDING_KEY);
          setLoading(false);
          return;
        }
        await setDoc(doc(db, 'stores', currentUser.uid), {
          storeName: pending.storeName || currentUser.displayName || '新しいお店',
          storeId: pending.storeId,
          email: currentUser.email,
          plan: 'free',
          emailOptIn: pending.emailOptIn || false,
          emailOptInAt: pending.emailOptIn ? new Date() : null,
          createdAt: new Date(),
        });
        await setDoc(doc(db, 'storeIds', pending.storeId), {
          uid: currentUser.uid,
        });
        sessionStorage.removeItem(PENDING_KEY);
        navigate('/dashboard');
      } catch {
        setError('Googleログインに失敗しました。');
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  async function checkStoreId(id) {
    if (!id) return;
    if (!/^[a-z0-9-]{6,30}$/.test(id)) {
      setIdError('6〜30文字の英小文字・数字・ハイフンのみ使用できます');
      return;
    }
    const snap = await getDoc(doc(db, 'storeIds', id));
    if (snap.exists()) {
      setIdError('このIDはすでに使われています');
    } else {
      setIdError('OK: 使用可能です');
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!/^[a-z0-9-]{6,30}$/.test(storeId)) {
      setError('店舗IDは6〜30文字の英小文字・数字・ハイフンのみ使用できます');
      return;
    }
    const snap = await getDoc(doc(db, 'storeIds', storeId));
    if (snap.exists()) {
      setError('この店舗IDはすでに使われています');
      return;
    }
    try {
      setError('');
      setLoading(true);
      const result = await register(email, password);
      await setDoc(doc(db, 'stores', result.user.uid), {
        storeName,
        storeId,
        email,
        plan: 'free',
        emailOptIn,
        emailOptInAt: emailOptIn ? new Date() : null,
        createdAt: new Date(),
      });
      await setDoc(doc(db, 'storeIds', storeId), {
        uid: result.user.uid,
      });
      navigate('/dashboard');
    } catch {
      setError('登録に失敗しました。もう一度お試しください。');
    }
    setLoading(false);
  }

  async function handleGoogle() {
    if (!/^[a-z0-9-]{6,30}$/.test(storeId)) {
      setError('先に店舗IDを入力してください（6〜30文字の英小文字・数字・ハイフン）');
      return;
    }
    const snap = await getDoc(doc(db, 'storeIds', storeId));
    if (snap.exists()) {
      setError('この店舗IDはすでに使われています');
      return;
    }
    try {
      setError('');
      setLoading(true);
      sessionStorage.setItem(PENDING_KEY, JSON.stringify({ storeId, storeName, emailOptIn }));
      await loginWithGoogle();
      // ここから先はGoogleへリダイレクトするため、戻ってきた後の処理は上のuseEffectで行う
    } catch {
      setError('Googleログインに失敗しました。');
      setLoading(false);
    }
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logoWrap}><Logo size={28} /></div>
        <p style={styles.subtitle}>新規登録（無料）</p>
        {error && <div style={styles.error}>{error}</div>}

        <div style={styles.field}>
          <label style={styles.label}>店舗ID（公開URL用）— 後から変更不可</label>
          <input type="text" value={storeId}
            onChange={(e) => {
              setStoreId(e.target.value.toLowerCase());
              setIdError('');
            }}
            onBlur={(e) => checkStoreId(e.target.value)}
            style={styles.input}
            placeholder="例：okinawa-shokudo"
            required />
          {idError && (
            <p style={{
              fontSize: '12px',
              marginTop: '4px',
              color: idError.startsWith('OK') ? '#2d6a4f' : '#c00'
            }}>{idError}</p>
          )}
          <p style={styles.hint}>
            あなたのメニューURL：<br />
            <strong>menusore.com/menu/{storeId || 'your-id'}</strong>
          </p>
        </div>

        <label style={styles.checkboxLabel}>
          <input type="checkbox" checked={emailOptIn}
            onChange={(e) => setEmailOptIn(e.target.checked)}
            style={styles.checkbox} />
          MenuSoreからの新機能やお知らせをメールで受け取る（任意）
        </label>

        <button onClick={handleGoogle} disabled={loading} style={styles.btnGoogle}>
          <img src="https://www.google.com/favicon.ico" alt="G" style={styles.googleIcon} />
          Googleで登録
        </button>

        <div style={styles.divider}><span>またはメールで登録</span></div>

        <form onSubmit={handleSubmit}>
          <div style={styles.field}>
            <label style={styles.label}>店舗名</label>
            <input type="text" value={storeName}
              onChange={(e) => setStoreName(e.target.value.slice(0, 20))}
              style={styles.input} maxLength={20}
              placeholder="例：沖縄食堂あじくーたー" required />
            <p style={{ fontSize: 11, color: '#a0aec0', margin: '4px 0 0', textAlign: 'right' }}>
              {storeName.length}/20文字
            </p>
          </div>
          <div style={styles.field}>
            <label style={styles.label}>メールアドレス</label>
            <input type="email" value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input} required />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>パスワード（6文字以上）</label>
            <input type="password" value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input} required />
          </div>
          <button type="submit" disabled={loading} style={styles.btn}>
            {loading ? '登録中...' : '無料で始める'}
          </button>
        </form>
        <p style={styles.link}>
          すでにアカウントをお持ちの方は <Link to="/login">ログイン</Link>
        </p>
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', display: 'flex', alignItems: 'center',
    justifyContent: 'center', background: '#f8f9fa' },
  card: { background: '#fff', padding: '40px', borderRadius: '16px',
    width: '100%', maxWidth: '420px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' },
  logoWrap: { display: 'flex', justifyContent: 'center', marginBottom: '16px' },
  subtitle: { textAlign: 'center', color: '#666', margin: '0 0 24px', fontSize: '16px' },
  error: { background: '#fee', color: '#c00', padding: '12px',
    borderRadius: '8px', marginBottom: '16px', fontSize: '14px' },
  field: { marginBottom: '16px' },
  label: { display: 'block', marginBottom: '6px', fontSize: '14px', color: '#333' },
  input: { width: '100%', padding: '12px', border: '1px solid #ddd',
    borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box' },
  hint: { fontSize: '12px', color: '#888', marginTop: '6px' },
  checkboxLabel: { display: 'flex', alignItems: 'center', gap: '8px',
    fontSize: '14px', color: '#333', marginBottom: '20px', cursor: 'pointer' },
  checkbox: { width: '18px', height: '18px', margin: 0, cursor: 'pointer',
    accentColor: '#2d6a4f', flexShrink: 0 },
  btnGoogle: { width: '100%', padding: '12px', background: '#fff', color: '#333',
    border: '2px solid #ddd', borderRadius: '8px', cursor: 'pointer',
    fontSize: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' },
  googleIcon: { width: '18px', height: '18px' },
  divider: { textAlign: 'center', margin: '20px 0', color: '#999',
    borderTop: '1px solid #eee', paddingTop: '20px' },
  btn: { width: '100%', padding: '14px', background: '#2d6a4f', color: '#fff',
    border: 'none', borderRadius: '8px', fontSize: '16px', cursor: 'pointer', marginTop: '8px' },
  link: { textAlign: 'center', marginTop: '20px', fontSize: '14px', color: '#666' },
};

export default Register;
